import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import api from '../../services/api';
import { X, Save, Plus, Trash2, Printer } from 'lucide-react';
import InvoiceTemplate from '../InvoiceTemplate/InvoiceTemplate';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency } from '../../utils/currency';

interface InvoiceFormProps {
    onClose: () => void;
    onSuccess: () => void;
    invoice?: any;
    customers: any[];
    invoiceProfile: any;
    invoices?: any[];
}

const InvoiceForm: React.FC<InvoiceFormProps> = ({ onClose, onSuccess, invoice, customers, invoiceProfile, invoices }) => {
    const { currency } = useAuth();
    const [submitting, setSubmitting] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [projects, setProjects] = useState<any[]>([]);

    const { register, control, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm({
        defaultValues: invoice || {
            invoiceNumber: `2026-${String((invoices?.length || 0) + 1).padStart(4, '0')}`,
            date: new Date().toISOString().split('T')[0],
            dueDate: '',
            customerId: '',
            status: 'Draft',
            currency: currency || 'EUR',
            quantityLabel: 'Quantity',
            items: [
                { description: '', quantity: 1, unitPrice: 0, total: 0 }
            ],
            notes: '',
            taxAmount: 0 // Used if standard tax applies
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "items"
    });

    const watchedItems = watch('items');

    // Auto calculate item totals when quantity or price change
    useEffect(() => {
        const subscription = watch((value, { name, type }) => {
            // Only trigger if name is items.X.quantity or items.X.unitPrice
            if (name?.startsWith('items.') && (name.endsWith('.quantity') || name.endsWith('.unitPrice'))) {
                const parts = name.split('.');
                const index = parseInt(parts[1], 10);
                const item = value.items?.[index];
                if (item) {
                    const total = (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0);
                    setValue(`items.${index}.total` as any, total, { shouldDirty: true });
                }
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, setValue]);

    const calculateSubtotal = () => {
        return watchedItems.reduce((acc: number, item: any) => acc + (parseFloat(item.total as any) || 0), 0);
    };

    const subtotal = calculateSubtotal();
    const taxAmount = parseFloat(watch('taxAmount') as any) || 0;
    const totalAmount = subtotal + taxAmount;

    // Fetch projects when customerId changes
    const selectedCustomerId = watch('customerId');
    useEffect(() => {
        const fetchProjects = async () => {
            if (!selectedCustomerId) {
                setProjects([]);
                return;
            }
            try {
                const res = await api.get(`/projects?customerId=${selectedCustomerId}`);
                setProjects(res.data);
            } catch (err) {
                console.error('Failed to fetch projects', err);
            }
        };
        fetchProjects();
    }, [selectedCustomerId]);

    const handleProjectImport = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const projectId = e.target.value;
        if (!projectId) return;

        const project = projects.find(p => p.id === projectId);
        if (!project) return;

        // If project has billable tasks, add them as line items
        const billableTasks = project.tasks?.filter((t: any) => t.isBillable) || [];

        if (billableTasks.length > 0) {
            billableTasks.forEach((task: any) => {
                append({
                    description: `${project.projectName} - ${task.taskName}`,
                    quantity: 1,
                    unitPrice: project.hourlyRate || 0,
                    total: project.hourlyRate || 0
                });
            });
        } else {
            // Otherwise just add a generic project line item
            append({
                description: project.projectName,
                quantity: 1,
                unitPrice: project.hourlyRate || 0,
                total: project.hourlyRate || 0
            });
        }

        // Reset the select element to allow importing again
        e.target.value = '';
    };

    // We can conditionally show a preview or standard form
    const onSubmit = async (data: any) => {
        setSubmitting(true);
        try {
            data.subtotal = subtotal;
            data.totalAmount = totalAmount;
            if (invoiceProfile) data.profileId = invoiceProfile.id;

            if (invoice) {
                await api.put(`/invoices/${invoice.id}`, data);
            } else {
                await api.post('/invoices', data);
            }
            onSuccess();
        } catch (err: any) {
            console.error('Failed to save invoice', err);
            alert(`Error: ${err.response?.data?.message || err.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    const handlePreview = () => {
        setShowPreview(true);
    };

    // Prepare data for the preview component
    const formValues = watch();
    const previewData = {
        ...formValues,
        customer: customers.find(c => c.id === formValues.customerId),
        subtotal,
        totalAmount
    };

    if (showPreview) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg w-full max-w-5xl h-[95vh] flex flex-col">
                    <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-lg">
                        <h2 className="text-xl font-semibold text-gray-800">Invoice Preview</h2>
                        <div className="flex gap-2">
                            <button
                                onClick={() => window.print()}
                                className="flex items-center gap-2 bg-slate-600 text-white px-4 py-2 rounded-md hover:bg-slate-700 transition"
                            >
                                <Printer size={18} /> Print
                            </button>
                            <button onClick={() => setShowPreview(false)} className="text-gray-500 hover:text-gray-700 bg-gray-200 px-3 py-2 rounded-md transition">
                                Back to Edit
                            </button>
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-8" id="printable-invoice">
                        {/* We hide UI elements not for print using CSS media queries in global, or via the component itself */}
                        <InvoiceTemplate invoice={previewData} profile={invoiceProfile} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-4xl h-[90vh] flex flex-col">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-lg">
                    <h2 className="text-xl font-semibold text-gray-800">
                        {invoice ? 'Edit Invoice' : 'New Invoice'}
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X size={24} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    <form id="invoice-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number*</label>
                                <input
                                    type="text"
                                    {...register('invoiceNumber', { required: 'Invoice Number is required' })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {errors.invoiceNumber && <p className="text-red-500 text-sm mt-1">{errors.invoiceNumber.message as string}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Customer*</label>
                                <select
                                    {...register('customerId', { required: 'Customer is required' })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Select a customer</option>
                                    {customers.map(c => (
                                        <option key={c.id} value={c.id}>{c.displayName}</option>
                                    ))}
                                </select>
                                {errors.customerId && <p className="text-red-500 text-sm mt-1">{errors.customerId.message as string}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Date*</label>
                                <input
                                    type="date"
                                    {...register('date', { required: 'Date is required' })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                <select
                                    {...register('status')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="Draft">Draft</option>
                                    <option value="Sent">Sent</option>
                                    <option value="Paid">Paid</option>
                                    <option value="Overdue">Overdue</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Item Label (Quantity/Hours)</label>
                                <div className="flex bg-gray-100 p-1 rounded-md w-fit">
                                    <button
                                        type="button"
                                        onClick={() => setValue('quantityLabel', 'Quantity')}
                                        className={`px-4 py-1.5 text-sm rounded-md transition ${watch('quantityLabel') === 'Quantity' ? 'bg-white shadow-sm text-blue-600 font-bold' : 'text-gray-500 hover:text-gray-700'}`}
                                    >
                                        Quantity
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setValue('quantityLabel', 'Hours')}
                                        className={`px-4 py-1.5 text-sm rounded-md transition ${watch('quantityLabel') === 'Hours' ? 'bg-white shadow-sm text-blue-600 font-bold' : 'text-gray-500 hover:text-gray-700'}`}
                                    >
                                        Hours
                                    </button>
                                </div>
                                <input type="hidden" {...register('quantityLabel')} />
                            </div>
                        </div>

                        <div className="pt-6 border-t border-gray-200">
                            <h3 className="text-lg font-medium text-gray-800 mb-4 flex justify-between items-center flex-wrap gap-4">
                                <span>Items</span>
                                <div className="flex items-center gap-3">
                                    {projects.length > 0 && (
                                        <select
                                            onChange={handleProjectImport}
                                            defaultValue=""
                                            className="text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                        >
                                            <option value="" disabled>Import from Project...</option>
                                            {projects.map(p => (
                                                <option key={p.id} value={p.id}>{p.projectName}</option>
                                            ))}
                                        </select>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => append({ description: '', quantity: 1, unitPrice: 0, total: 0 })}
                                        className="flex items-center gap-1 text-sm bg-gray-100 text-gray-700 font-medium px-3 py-1.5 rounded-md hover:bg-gray-200 transition whitespace-nowrap"
                                    >
                                        <Plus size={16} /> Add Line Item
                                    </button>
                                </div>
                            </h3>

                            <table className="w-full text-left bg-white border border-gray-200 rounded-md overflow-hidden shadow-sm">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase w-1/2">Description</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase">{watch('quantityLabel')}</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Unit Price</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Total</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase w-12 text-center"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {fields.map((field, index) => (
                                        <tr key={field.id} className="hover:bg-slate-50 transition">
                                            <td className="px-4 py-2">
                                                <input
                                                    {...register(`items.${index}.description` as const, { required: true })}
                                                    className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                                    placeholder="Item description"
                                                />
                                            </td>
                                            <td className="px-4 py-2">
                                                <input
                                                    type="number"
                                                    step="any"
                                                    {...register(`items.${index}.quantity` as const, { required: true })}
                                                    className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-right"
                                                />
                                            </td>
                                            <td className="px-4 py-2">
                                                <input
                                                    type="number"
                                                    step="any"
                                                    {...register(`items.${index}.unitPrice` as const, { required: true })}
                                                    className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-right"
                                                />
                                            </td>
                                            <td className="px-4 py-2">
                                                <input
                                                    type="number"
                                                    step="any"
                                                    readOnly
                                                    {...register(`items.${index}.total` as const)}
                                                    className="w-full px-2 py-1 border-transparent bg-transparent rounded text-right font-medium"
                                                />
                                            </td>
                                            <td className="px-4 py-2 text-center">
                                                <button
                                                    type="button"
                                                    onClick={() => remove(index)}
                                                    disabled={fields.length === 1}
                                                    className={`text-red-500 hover:bg-red-50 p-1.5 rounded-md transition ${fields.length === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <div className="flex justify-end mt-4">
                                <div className="w-1/3 space-y-3">
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-gray-600 font-medium">Subtotal</span>
                                        <span className="font-semibold text-gray-800">{formatCurrency(subtotal, currency)}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-sm border-t border-gray-200 pt-3">
                                        <span className="text-gray-900 font-bold text-base">Total</span>
                                        <span className="font-bold text-blue-600 text-lg">{formatCurrency(totalAmount, currency)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="pt-6 border-t border-gray-200">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Notes / Terms</label>
                            <textarea
                                {...register('notes')}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Payment terms, thank you note..."
                            />
                        </div>
                    </form>
                </div>

                <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-between">
                    <button
                        type="button"
                        onClick={handlePreview}
                        className="flex items-center gap-2 bg-slate-600 text-white px-6 py-2 rounded-md hover:bg-slate-700 transition"
                    >
                        <Printer size={18} />
                        Preview & Print
                    </button>
                    <div className="flex gap-3">
                        <button
                            type="submit"
                            form="invoice-form"
                            disabled={submitting}
                            className={`flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition ${submitting ? 'opacity-70 cursor-wait' : ''}`}
                        >
                            <Save size={18} />
                            {submitting ? 'Saving...' : 'Save'}
                        </button>
                        <button
                            onClick={onClose}
                            className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-100 transition text-gray-700"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InvoiceForm;
